import {
    DefaultProps,
    EditorPublicType,
    StructuredEditVisitor,
    visit,
} from "@/model/structured-edit";
import { v7 } from "uuid";
import { openSelectModal } from "@/composables/modal";

const camelToKebab = (str: string): string => {
    return str
        .split("")
        .map((s) => (s.toUpperCase() === s ? ["-", s.toLowerCase()] : [s]))
        .flat()
        .join("");
};

function visitToggle<const K extends string, const T extends boolean>(
    name: K,
    defaultValue: T,
    icon?: string,
) {
    return visit(
        name,
        {
            type: "boolean",
        },
        {
            kind: "switch",
            name(props: DefaultProps, _val) {
                return props.$t(`mute-weak-option-${camelToKebab(name)}`);
            },
            icon: icon
                ? (_props: DefaultProps, _val) => {
                      return icon;
                  }
                : undefined,
        },
        (_props: DefaultProps) => defaultValue as boolean,
    );
}

function visitId<const K extends string>(name: K) {
    return visit(
        name,
        {
            type: "string",
        },
        {
            kind: "hidden",
        },
        (_props: DefaultProps) => v7() as string,
    );
}

export const muteRuleMatchContentBase = new StructuredEditVisitor({})
    .map(visitToggle("caseSensitive", false, "text-aa"))
    .map(visitToggle("withinAttachments", true, "paperclip"))
    .map(visitToggle("withinText", true, "text-align-left"))
    .map(visitToggle("withinCw", true, "receipt"))
    .build({
        name(props: DefaultProps, _val) {
            return props.$t("mute-weak-rule-options");
        },
    });

export type MuteRuleMatchContentBase = EditorPublicType<
    typeof muteRuleMatchContentBase
>;

export const muteLocationBase = new StructuredEditVisitor({})
    .map(visitToggle("withinParent", true, "arrow-u-up-left"))
    .map(visitToggle("withinQuote", true, "quotes"))
    .map(visitToggle("withinRenote", true, "repeat"))
    .map(visitToggle("withinNote", true, "note"))
    .build({
        name(props: DefaultProps, val) {
            return props.$t(`mute-weak-ruleset-options`);
        },
    });

export type MuteLocationBase = EditorPublicType<typeof muteLocationBase>;

export const muteRule = new StructuredEditVisitor({})
    .map(visitId("id"))
    .map(
        visit(
            "kind",
            {
                type: "string",
                enum: [
                    "match-string",
                    "match-words",
                    "match-mentions",
                    "match-usernames",
                    "match-many-mentions",
                ] as const,
            },
            {
                kind: "hidden",
                name(props: DefaultProps, _val) {
                    return props.$t(`mute-weak-rule-kind`);
                },
                enumName(props: DefaultProps, val) {
                    return props.$t(`mute-weak-rule-${val}`);
                },
            },
            (_props: DefaultProps) =>
                "match-string" as
                    | "match-string"
                    | "match-words"
                    | "match-mentions"
                    | "match-usernames"
                    | "match-many-mentions",
        ),
    )
    .build({
        name(props: DefaultProps, val) {
            return props.$t(`mute-weak-rule-${val.kind}`);
        },
    });

export const matchString = new StructuredEditVisitor({
    ...muteRule.editor.properties,
})
    .discriminant("kind", "match-string")
    .map(
        visit(
            "content",
            muteRuleMatchContentBase.schema,
            {
                ...muteRuleMatchContentBase.editor,
                kind: "fieldset",
                layout: "rows",
                collapsedLayout: "grid",
            },
            muteRuleMatchContentBase.editor.defaultValue,
        ),
    )
    .map(
        visit(
            "strings",
            {
                type: "array",
                items: { type: "string" },
            },
            {
                kind: "list-editor",
                fieldset: true,
                using: (_val) =>
                    ({
                        kind: "field",
                    }) as const,
                factory(_props) {
                    return "";
                },
                name(props: DefaultProps, _val) {
                    return props.$t("mute-weak-rule-strings");
                },
                addName(props: DefaultProps, _val) {
                    return props.$t("mute-weak-rule-add-string");
                },
            },
            (_props: DefaultProps) => [] as string[],
        ),
    )
    .build({
        name(props: DefaultProps, val) {
            return props.$t(`mute-weak-rule-${val.kind}`);
        },
    });

export type MuteRuleMatchString = EditorPublicType<typeof matchString>;

export const matchWords = new StructuredEditVisitor({
    ...muteRule.editor.properties,
})
    .discriminant("kind", "match-words")
    .map(
        visit(
            "content",
            muteRuleMatchContentBase.schema,
            {
                ...muteRuleMatchContentBase.editor,
                kind: "fieldset",
                layout: "rows",
                collapsedLayout: "grid",
            },
            muteRuleMatchContentBase.editor.defaultValue,
        ),
    )
    .map(
        visit(
            "words",
            {
                type: "array",
                items: { type: "string" },
            },
            {
                kind: "list-editor",
                fieldset: true,
                using: (_val) =>
                    ({
                        kind: "field",
                    }) as const,
                factory(_props) {
                    return "";
                },
                name(props: DefaultProps, _val) {
                    return props.$t("mute-weak-rule-words");
                },
                addName(props: DefaultProps, _val) {
                    return props.$t("mute-weak-rule-add-word");
                },
            },
            (_props: DefaultProps) => [] as string[],
        ),
    )
    .build({
        name(props: DefaultProps, val) {
            return props.$t(`mute-weak-rule-${val.kind}`);
        },
    });

export type MuteRuleMatchWords = EditorPublicType<typeof matchWords>;

export const matchMentions = new StructuredEditVisitor({
    ...muteRule.editor.properties,
})
    .discriminant("kind", "match-mentions")
    .map(
        visit(
            "content",
            muteRuleMatchContentBase.schema,
            {
                ...muteRuleMatchContentBase.editor,
                kind: "fieldset",
                layout: "rows",
                collapsedLayout: "grid",
            },
            muteRuleMatchContentBase.editor.defaultValue,
        ),
    )
    .map(
        visit(
            "tags",
            {
                type: "array",
                items: { type: "string" },
            },
            {
                kind: "user-picker",
                name(props: DefaultProps, _val) {
                    return props.$t("mute-weak-option-mentioned-users");
                },
            },
            (_props: DefaultProps) => [] as string[],
        ),
    )
    .build({
        name(props: DefaultProps, val) {
            return props.$t(`mute-weak-rule-${val.kind}`);
        },
    });

export type MuteRuleMatchMentions = EditorPublicType<typeof matchMentions>;

export const matchUsernames = new StructuredEditVisitor({
    ...muteRule.editor.properties,
})
    .discriminant("kind", "match-usernames")
    .map(
        visit(
            "content",
            muteRuleMatchContentBase.schema,
            {
                ...muteRuleMatchContentBase.editor,
                kind: "fieldset",
                layout: "rows",
                collapsedLayout: "grid",
            },
            muteRuleMatchContentBase.editor.defaultValue,
        ),
    )
    .map(
        visit(
            "tags",
            {
                type: "array",
                items: { type: "string" },
            },
            {
                kind: "user-picker",
                name(props: DefaultProps, _val) {
                    return props.$t("mute-weak-option-post-authors");
                },
            },
            (_props: DefaultProps) => [] as string[],
        ),
    )
    .build({
        name(props: DefaultProps, val) {
            return props.$t(`mute-weak-rule-${val.kind}`);
        },
    });

export type MuteRuleMatchUsernames = EditorPublicType<typeof matchUsernames>;

export const matchManyMentions = new StructuredEditVisitor({
    ...muteRule.editor.properties,
})
    .discriminant("kind", "match-many-mentions")
    .map(
        visit(
            "content",
            muteRuleMatchContentBase.schema,
            {
                ...muteRuleMatchContentBase.editor,
                kind: "fieldset",
                layout: "rows",
                collapsedLayout: "grid",
            },
            muteRuleMatchContentBase.editor.defaultValue,
        ),
    )
    .map(
        visit(
            "limit",
            {
                type: "integer",
                minimum: 1,
            },
            {
                kind: "number-field",
            },
            (_props: DefaultProps) => 8 as number,
        ),
    )
    .build({
        name(props: DefaultProps, val) {
            return props.$t(`mute-weak-rule-${val.kind}`);
        },
    });

export type MuteRuleMatchManyMentions = EditorPublicType<
    typeof matchManyMentions
>;

export type MuteRule =
    | MuteRuleMatchString
    | MuteRuleMatchWords
    | MuteRuleMatchMentions
    | MuteRuleMatchManyMentions
    | MuteRuleMatchUsernames;

export const muteRulesEnum = {
    [matchString.schema.properties.kind.const]: matchString,
    [matchWords.schema.properties.kind.const]: matchWords,
    [matchMentions.schema.properties.kind.const]: matchMentions,
    [matchManyMentions.schema.properties.kind.const]: matchManyMentions,
    [matchUsernames.schema.properties.kind.const]: matchUsernames,
};

export const muteRuleset = new StructuredEditVisitor({})
    .map(visitId("id"))
    .map(
        visit(
            "name",
            {
                type: "string",
            },
            {
                kind: "hidden",
            },
            (_props: DefaultProps) => _props.$t("mute-weak-unnamed") as string,
        ),
    )
    .map(
        visit(
            "location",
            muteLocationBase.schema,
            {
                ...muteLocationBase.editor,
                kind: "fieldset",
                layout: "rows",
                collapsedLayout: "grid",
            },
            muteLocationBase.editor.defaultValue,
        ),
    )
    .map(
        visit(
            "rules",
            {
                anyOf: [],
                type: "array",
                items: {
                    type: "object",
                    discriminator: {
                        propertyName: "kind",
                    },
                    required: ["kind"],
                    oneOf: [
                        matchString.schema,
                        matchWords.schema,
                        matchMentions.schema,
                        matchManyMentions.schema,
                        matchUsernames.schema,
                    ],
                } as const,
            },
            {
                kind: "list-editor",
                using: (val) => muteRulesEnum[val.kind].editor,
                key: (val) => val.id,
                async factory(props: DefaultProps) {
                    return await openSelectModal({
                        title: props.$t("mute-weak-rules-add"),
                        items: muteRule.editor.properties.kind.meta.enum.map(
                            (rule) => ({
                                text: props.$t(`mute-weak-rule-${rule}`),
                                value: rule,
                            }),
                        ),
                        defaultValue: muteRule.editor.defaultValue(props).kind,
                    })
                        .then((result) =>
                            muteRulesEnum[result].editor.defaultValue(props),
                        )
                        .catch(() => null);
                },
                name: (_props: DefaultProps, val: MuteRule) => {
                    return val.kind;
                },
                addName(props: DefaultProps, _val) {
                    return props.$t("mute-weak-rules-add");
                },
            },
            (_props: DefaultProps) => [] as MuteRule[],
        ),
    )
    .build({});

export type MuteRuleset = EditorPublicType<typeof muteRuleset>;
